import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MantineReactTable, MRT_SelectCheckbox, useMantineReactTable } from "mantine-react-table";
import { useEffect, useMemo, useState } from "react";
import { IconFilter2, IconSearch } from "@tabler/icons-react";
import { ActionIcon, Divider, Flex, Group, Popover, Select, Stack, Text, useMatches } from "@mantine/core";
import { useDebouncedValue, useSetState } from "@mantine/hooks";
import { parseAsInteger, useQueryStates } from "nuqs";
import { useTranslation } from "react-i18next";
import classes from "./DataTableUi.module.css";

export const DataTableUi = (props) => {

    const { t } = useTranslation();

    //TODO: refactor

    const { columns = [], url = '', tableKey = '', collapseFiltersOnPhone = false, phoneToolbarAction = null, mantineTopToolbarProps = {}, ...other } = props;
    const compactPagination = useMatches({ base: true, sm: false }) && collapseFiltersOnPhone;

    const [pagination, setPagination] = useQueryStates({
        pageIndex: parseAsInteger.withDefault(0),
        pageSize: parseAsInteger.withDefault(10),
    });
    const [globalFilter, setGlobalFilter] = useState(null);
    const [filters, setFilters] = useSetState({});
    const [debouncedGlobalFilter] = useDebouncedValue(globalFilter, 400);

    const withPageReset = (setState) => (value) => {
        setState(value);
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    };

    const queryClient = useQueryClient();
    const queryKey = ['ssr-datatable-' + tableKey, debouncedGlobalFilter, pagination, filters];

    const { data: queryData, isFetching, refetch } = useQuery({
        queryKey,
        queryFn: async () => {

            const params = {
                start: pagination?.pageIndex * pagination?.pageSize,
                length: pagination?.pageSize,
                search: debouncedGlobalFilter,
                ...filters
            }

            const { data } = await axios.get(url, {
                params
            });
            return data || [];
        },
    });

    useEffect(() => {
        if (queryData) props.onDataChange?.(queryData);
    }, [queryData]);

    const skeletonRows = useMemo(
        () => Array.from({ length: pagination?.pageSize || 10 }, () => ({})),
        [pagination?.pageSize]
    );

    // Patches a single row in the cached query data in place, without
    // refetching — so status-only changes (pause/resume, etc.) don't
    // trigger the skeleton-loading flash across the whole table.
    const updateRow = (id, patch) => {
        queryClient.setQueryData(queryKey, (old) => {
            if (!old?.data) return old;
            return {
                ...old,
                data: old.data.map((row) => (row.id === id ? { ...row, ...patch } : row)),
            };
        });
    };

    const table = useMantineReactTable({
        columns: columns.map((column) => ({ enableColumnFilter: false, enableSorting: false, ...column })),
        data: isFetching ? skeletonRows : (queryData?.data || []),
        rowCount: queryData?.recordsFiltered || 0,
        initialState: { showGlobalFilter: true },
        state: { pagination, globalFilter, showSkeletons: isFetching },

        enableFullScreenToggle: false,
        enableDensityToggle: false,
        enableColumnResizing: false,
        enableColumnPinning: false,
        enableColumnPinning: false,
        enableRowActions: true,
        enableRowSelection: true,
        positionActionsColumn: "last",
        enableSorting: false,
        enableHiding: false,
        enableColumnActions: false,
        mantinePaperProps: {
            shadow: 'none',
            radius: "lg",
        },

        //Column Filter
        manualFiltering: true,
        enableFilters: false,
        enableColumnFilters: false,
        enableFilters: false,

        //Global filter a.k.a search input
        enableGlobalFilter: true,
        positionGlobalFilter: "left",
        onGlobalFilterChange: withPageReset(setGlobalFilter),
        mantineSearchTextInputProps: {
            placeholder: `Search`,
            miw: 300,
            variant: 'default',
            radius: "md",
            leftSection: null,
            rightSection: <IconSearch />,
            clearable: true,
            className: classes.search,
            style: {
                border: '1px solid var(--search-bd)',
                background: 'var(--search-bg)',
            },

        },

        mantineTopToolbarProps: {
            m: 0,
            p: 8,
            ...mantineTopToolbarProps,
            className: [collapseFiltersOnPhone && classes.phoneToolbar, mantineTopToolbarProps.className].filter(Boolean).join(' ') || undefined,
        },

        //Pagination
        manualPagination: true,
        paginationDisplayMode: "pages",
        positionPagination: "bottom",
        onPaginationChange: setPagination,
        mantinePaginationProps: {
            showRowsPerPage: false,
            radius: "md",
            ...(compactPagination && { siblings: 0, boundaries: 0, withEdges: false }),
        },

        mantineSelectCheckboxProps: {
            size: "sm",
            radius: 4,
        },
        mantineSelectAllCheckboxProps: {
            size: "sm",
            radius: 4,
        },

        //head
        mantineTableHeadCellProps: {
            align: "left",
            p: 12,
            className: classes.headCell,
            bg: 'var(--head-bg)',
            style: {
                borderTop: '1px solid var(--head-bd)',
                borderBottom: '1px solid var(--head-bd)',
                fontSize: 14,
                whiteSpace: 'normal',
                overflowWrap: 'anywhere',
            }
        },

        //body
        mantineTableBodyCellProps: {
            align: "left",
            px: 12,
            py: 8,
        },

        meta: {
            refetch,
            updateRow,
        },

        renderTopToolbarCustomActions: ({ table }) => {
            const customFilters = props?.renderCustomFilters
                ? props.renderCustomFilters({ filters, setFilters: withPageReset(setFilters), table })
                : null;

            return (
                <Group
                    w="100%"
                    justify="space-between"
                    pl={4}
                >
                    <Flex
                        gap={4}
                        justify="flex-start"
                        align="center"
                        direction="row"
                        wrap="wrap"
                    >
                        <Divider orientation="vertical" mx={16} visibleFrom={collapseFiltersOnPhone ? 'sm' : undefined} />
                        {collapseFiltersOnPhone ? (
                            // AD-230: a phone has no room for the filters, so they open from a button.
                            <>
                                <Group gap={4} visibleFrom="sm">{customFilters}</Group>
                                <Group gap={12} wrap="nowrap" hiddenFrom="sm">
                                    {customFilters && (
                                        <Popover position="bottom-end" shadow="md" radius="md" keepMounted>
                                            <Popover.Target>
                                                <ActionIcon variant="default" size={36} radius={8} aria-label={t('table.filters')}>
                                                    <IconFilter2 size={16} />
                                                </ActionIcon>
                                            </Popover.Target>
                                            <Popover.Dropdown>
                                                <Stack gap="sm">{customFilters}</Stack>
                                            </Popover.Dropdown>
                                        </Popover>
                                    )}
                                    {phoneToolbarAction}
                                </Group>
                            </>
                        ) : customFilters}
                    </Flex>
                    {
                        // AD-188: a table can put its own control on the right (User
                        // Management's "Invite User"); every other table keeps this side.
                        props?.toolbarEnd ? props.toolbarEnd : props?.enableRowSizeSelection ? (
                            <Group spacing={4} align="center" visibleFrom={collapseFiltersOnPhone ? 'sm' : undefined}>
                                <Text fz={14} fw="400" lh="normal">{t('table.showingPerPage')}</Text>
                                <Select
                                    value={String(pagination?.pageSize)}
                                    onChange={(value) => {
                                        setPagination((prev) => ({
                                            ...prev,
                                            pageSize: parseInt(value, 10),
                                            pageIndex: 0,
                                        }));
                                    }}
                                    data={['10', '20', '50', '100']}
                                    className={classes.pageSize}
                                    classNames={{ dropdown: classes.pageSizeDropdown }}
                                    size="xs"
                                    w={60}
                                />
                            </Group>
                        ) : <Text fz={14} fw="400" lh="normal" visibleFrom={collapseFiltersOnPhone ? 'sm' : undefined}>{t('table.totalRows', { count: queryData?.recordsFiltered || '-' })}</Text>
                    }

                </Group>
            );
        },

        ...other
    });

    return (
        <>
            <MantineReactTable table={table} />
        </>
    )
}
